import { isEmpty } from "../../helpers/utils";

/**
 * Converts a formatted currency string into a float string without formatting.
 *
 * This function processes the input string by removing thousands separators (commas or spaces)
 * and replacing decimal separators (commas or dots) with a standard dot. It ensures the resulting
 * string represents a valid float value.
 *
 * @param value - The formatted currency string to be converted.
 * @returns The float string without formatting, or an empty string if the input is invalid.
 */
export const valueWithoutFormat = (value?: string | null) => {
  if (isEmpty(value)) return "";

  if (!value?.toString().includes(".") && !value?.toString().includes(",")) {
    return value ?? "";
  }

  // Check if negative
  const isNegative = value?.toString().includes("-");

  // Remove everything except digits
  value = value?.toString().replace(/[^\d]/g, "");

  // Get the last two digits as the decimal part
  const decimalPart = value?.slice(-2);
  const integerPart = value?.slice(0, -2);

  // Combine them into a proper float
  if (isEmpty(integerPart) && isEmpty(decimalPart)) return "";
  let result = `${integerPart}.${decimalPart}`;
  if (isNegative) result = `-${result}`;
  return result;
};

export const valueWithoutFormatInFloat = (value?: string | null) => {
  const formattedValue = valueWithoutFormat(value);
  if (formattedValue === "") {
    return 0;
  }
  const number = parseFloat(formattedValue);
  return isNaN(number) ? 0 : number;
};

/**
 * Formats a given number value into a string based on the specified locale.
 *
 * @param numberValue - The numeric value to be formatted.
 * @param locale - The locale string to format the number.
 * @returns The formatted string representation of the number.
 */
export const getFormattedValue = (
  numberValue: number,
  locale: string,
  allowDecimal = true,
  useGrouping = true,
) => {
  const currencyFormatter = new Intl.NumberFormat(locale, {
    style: "decimal",
    minimumFractionDigits: allowDecimal ? 2 : 0,
    useGrouping,
  });
  if (isNaN(numberValue)) {
    return "";
  }

  const valueToFormat = allowDecimal ? numberValue / 100 : numberValue;
  return currencyFormatter.format(valueToFormat);
};

export const COUNTRY_CURRENCY_MAP = {
  AD: "EUR",
  AE: "AED",
  AF: "AFN",
  AG: "XCD",
  AI: "XCD",
  AL: "ALL",
  AM: "AMD",
  AN: "ANG",
  AO: "AOA",
  AQ: "USD",
  AR: "ARS",
  AS: "USD",
  AT: "EUR",
  AU: "AUD",
  AW: "AWG",
  AX: "EUR",
  AZ: "AZN",
  BA: "BAM",
  BB: "BBD",
  BD: "BDT",
  BE: "EUR",
  BF: "XOF",
  BG: "BGN",
  BH: "BHD",
  BI: "BIF",
  BJ: "XOF",
  BL: "EUR",
  BM: "BMD",
  BN: "BND",
  BO: "BOB",
  BQ: "USD",
  BR: "BRL",
  BS: "BSD",
  BT: "BTN",
  BV: "NOK",
  BW: "BWP",
  BY: "BYN",
  BZ: "BZD",
  CA: "CAD",
  CC: "AUD",
  CD: "CDF",
  CF: "XAF",
  CG: "XAF",
  CH: "CHF",
  CI: "XOF",
  CK: "NZD",
  CL: "CLP",
  CM: "XAF",
  CN: "CNY",
  CO: "COP",
  CR: "CRC",
  CU: "CUP",
  CV: "CVE",
  CW: "ANG",
  CX: "AUD",
  CY: "EUR",
  CZ: "CZK",
  DE: "EUR",
  DJ: "DJF",
  DK: "DKK",
  DM: "XCD",
  DO: "DOP",
  DZ: "DZD",
  EC: "USD",
  EE: "EUR",
  EG: "EGP",
  EH: "MAD",
  ER: "ERN",
  ES: "EUR",
  ET: "ETB",
  FI: "EUR",
  FJ: "FJD",
  FK: "FKP",
  FM: "USD",
  FO: "DKK",
  FR: "EUR",
  GA: "XAF",
  GB: "GBP",
  GD: "XCD",
  GE: "GEL",
  GF: "EUR",
  GG: "GBP",
  GH: "GHS",
  GI: "GIP",
  GL: "DKK",
  GM: "GMD",
  GN: "GNF",
  GP: "EUR",
  GQ: "XAF",
  GR: "EUR",
  GS: "FKP",
  GT: "GTQ",
  GU: "USD",
  GW: "XOF",
  GY: "GYD",
  HK: "HKD",
  HM: "AUD",
  HN: "HNL",
  HR: "EUR",
  HT: "HTG",
  HU: "HUF",
  ID: "IDR",
  IE: "EUR",
  IL: "ILS",
  IM: "GBP",
  IN: "INR",
  IO: "USD",
  IQ: "IQD",
  IR: "IRR",
  IS: "ISK",
  IT: "EUR",
  JE: "GBP",
  JM: "JMD",
  JO: "JOD",
  JP: "JPY",
  KE: "KES",
  KG: "KGS",
  KH: "KHR",
  KI: "AUD",
  KM: "KMF",
  KN: "XCD",
  KP: "KPW",
  KR: "KRW",
  KW: "KWD",
  KY: "KYD",
  KZ: "KZT",
  LA: "LAK",
  LB: "LBP",
  LC: "XCD",
  LI: "CHF",
  LK: "LKR",
  LR: "LRD",
  LS: "LSL",
  LT: "EUR",
  LU: "EUR",
  LV: "EUR",
  LY: "LYD",
  MA: "MAD",
  MC: "EUR",
  MD: "MDL",
  ME: "EUR",
  MF: "EUR",
  MG: "MGA",
  MH: "USD",
  MK: "MKD",
  ML: "XOF",
  MM: "MMK",
  MN: "MNT",
  MO: "MOP",
  MP: "USD",
  MQ: "EUR",
  MR: "MRU",
  MS: "XCD",
  MT: "EUR",
  MU: "MUR",
  MV: "MVR",
  MW: "MWK",
  MX: "MXN",
  MY: "MYR",
  MZ: "MZN",
  NA: "NAD",
  NC: "XPF",
  NE: "XOF",
  NF: "AUD",
  NG: "NGN",
  NI: "NIO",
  NL: "EUR",
  NO: "NOK",
  NP: "NPR",
  NR: "AUD",
  NU: "NZD",
  NZ: "NZD",
  OM: "OMR",
  PA: "PAB",
  PE: "PEN",
  PF: "XPF",
  PG: "PGK",
  PH: "PHP",
  PK: "PKR",
  PL: "PLN",
  PM: "EUR",
  PN: "NZD",
  PR: "USD",
  PS: "ILS",
  PT: "EUR",
  PW: "USD",
  PY: "PYG",
  QA: "QAR",
  RE: "EUR",
  RO: "RON",
  RS: "RSD",
  RU: "RUB",
  RW: "RWF",
  SA: "SAR",
  SB: "SBD",
  SC: "SCR",
  SD: "SDG",
  SE: "SEK",
  SG: "SGD",
  SH: "SHP",
  SI: "EUR",
  SJ: "NOK",
  SK: "EUR",
  SL: "SLE",
  SM: "EUR",
  SN: "XOF",
  SO: "SOS",
  SR: "SRD",
  SS: "SSP",
  ST: "STN",
  SV: "USD",
  SX: "ANG",
  SY: "SYP",
  SZ: "SZL",
  TC: "USD",
  TD: "XAF",
  TF: "EUR",
  TG: "XOF",
  TH: "THB",
  TJ: "TJS",
  TK: "NZD",
  TL: "USD",
  TM: "TMT",
  TN: "TND",
  TO: "TOP",
  TR: "TRY",
  TT: "TTD",
  TV: "AUD",
  TW: "TWD",
  TZ: "TZS",
  UA: "UAH",
  UG: "UGX",
  UM: "USD",
  US: "USD",
  UY: "UYU",
  UZ: "UZS",
  VA: "EUR",
  VC: "XCD",
  VE: "VED",
  VG: "USD",
  VI: "USD",
  VN: "VND",
  VU: "VUV",
  WF: "XPF",
  WS: "WST",
  XK: "EUR",
  YE: "YER",
  YT: "EUR",
  ZA: "ZAR",
  ZM: "ZMW",
  ZW: "ZWG",
} as const;
export interface CurrencyFormat {
  locale: string;
}

export const CURRENCY_FORMAT_MAP: Record<string, CurrencyFormat> = {
  USD: {
    locale: "en-US",
  },
  EUR: {
    locale: "de-DE",
  },
  INR: {
    locale: "en-IN",
  },
  GBP: {
    locale: "en-GB",
  },
  JPY: {
    locale: "ja-JP",
  },
  AFN: {
    locale: "en-US",
  },
  ALL: {
    locale: "sq-AL",
  },
  DZD: {
    locale: "en-US",
  },
  AOA: {
    locale: "pt-AO",
  },
  XCD: {
    locale: "en-001",
  },
  ARS: {
    locale: "es-AR",
  },
  AMD: {
    locale: "hy-AM",
  },
  AWG: {
    locale: "nl-AW",
  },
  AUD: {
    locale: "en-AU",
  },
  AZN: {
    locale: "az-Latn-AZ",
  },
  BSD: {
    locale: "en-BS",
  },
  BHD: {
    locale: "en-US",
  },
  BDT: {
    locale: "en-US",
  },
  BBD: {
    locale: "en-BB",
  },
  BYN: {
    locale: "be-BY",
  },
  BZD: {
    locale: "en-BZ",
  },
  XOF: {
    locale: "fr-SN",
  },
  BMD: {
    locale: "en-BM",
  },
  BTN: {
    locale: "dz-BT",
  },
  BOB: {
    locale: "es-BO",
  },
  BAM: {
    locale: "bs-Cyrl-BA",
  },
  BWP: {
    locale: "en-BW",
  },
  NOK: {
    locale: "nb-NO",
  },
  BRL: {
    locale: "pt-BR",
  },
  BND: {
    locale: "ms-BN",
  },
  BGN: {
    locale: "bg-BG",
  },
  BIF: {
    locale: "fr-BI",
  },
  KHR: {
    locale: "km-KH",
  },
  XAF: {
    locale: "fr-CM",
  },
  CAD: {
    locale: "en-CA",
  },
  KYD: {
    locale: "en-KY",
  },
  CLF: {
    locale: "es-CL",
  },
  CLP: {
    locale: "es-CL",
  },
  CNY: {
    locale: "zh-CN",
  },
  COP: {
    locale: "es-CO",
  },
  COU: {
    locale: "es-CO",
  },
  KMF: {
    locale: "ar-KM",
  },
  CDF: {
    locale: "fr-CD",
  },
  NZD: {
    locale: "en-NZ",
  },
  CRC: {
    locale: "es-CR",
  },
  HRK: {
    locale: "hr-HR",
  },
  CUC: {
    locale: "es-CU",
  },
  CUP: {
    locale: "es-CU",
  },
  ANG: {
    locale: "nl-CW",
  },
  CZK: {
    locale: "cs-CZ",
  },
  DKK: {
    locale: "da-DK",
  },
  DJF: {
    locale: "fr-DJ",
  },
  DOP: {
    locale: "es-DO",
  },
  EGP: {
    locale: "en-US",
  },
  SVC: {
    locale: "es-SV",
  },
  ERN: {
    locale: "ti-ER",
  },
  ETB: {
    locale: "am-ET",
  },
  FJD: {
    locale: "en-FJ",
  },
  XPF: {
    locale: "fr-PF",
  },
  GMD: {
    locale: "en-GM",
  },
  GEL: {
    locale: "ka-GE",
  },
  GHS: {
    locale: "en-GH",
  },
  GIP: {
    locale: "en-GI",
  },
  GTQ: {
    locale: "es-GT",
  },
  GNF: {
    locale: "fr-GN",
  },
  GYD: {
    locale: "en-GY",
  },
  HTG: {
    locale: "fr-HT",
  },
  HNL: {
    locale: "es-HN",
  },
  HKD: {
    locale: "zh-HK",
  },
  HUF: {
    locale: "hu-HU",
  },
  ISK: {
    locale: "is-IS",
  },
  IDR: {
    locale: "id-ID",
  },
  IRR: {
    locale: "fa-IR",
  },
  IQD: {
    locale: "en-US",
  },
  ILS: {
    locale: "he-IL",
  },
  JMD: {
    locale: "en-JM",
  },
  JOD: {
    locale: "en-US",
  },
  KZT: {
    locale: "kk-KZ",
  },
  KES: {
    locale: "sw-KE",
  },
  KPW: {
    locale: "ko-KP",
  },
  KRW: {
    locale: "ko-KR",
  },
  KWD: {
    locale: "en-US",
  },
  KGS: {
    locale: "ky-KG",
  },
  LAK: {
    locale: "lo-LA",
  },
  LBP: {
    locale: "en-US",
  },
  LSL: {
    locale: "st-LS",
  },
  ZAR: {
    locale: "en-ZA",
  },
  LRD: {
    locale: "en-LR",
  },
  LYD: {
    locale: "en-US",
  },
  CHF: {
    locale: "de-CH",
  },
  MOP: {
    locale: "zh-MO",
  },
  MGA: {
    locale: "mg-MG",
  },
  MWK: {
    locale: "en-MW",
  },
  MYR: {
    locale: "ms-MY",
  },
  MVR: {
    locale: "dv-MV",
  },
  MRU: {
    locale: "en-US",
  },
  MUR: {
    locale: "mfe-MU",
  },
  MXN: {
    locale: "es-MX",
  },
  MDL: {
    locale: "ro-MD",
  },
  MNT: {
    locale: "mn-MN",
  },
  MAD: {
    locale: "en-US",
  },
  MZN: {
    locale: "pt-MZ",
  },
  MMK: {
    locale: "my-MM",
  },
  NAD: {
    locale: "en-NA",
  },
  NPR: {
    locale: "ne-NP",
  },
  NIO: {
    locale: "es-NI",
  },
  NGN: {
    locale: "en-NG",
  },
  OMR: {
    locale: "ar-OM",
  },
  PKR: {
    locale: "ur-PK",
  },
  PAB: {
    locale: "es-PA",
  },
  PGK: {
    locale: "en-PG",
  },
  PYG: {
    locale: "gn-PY",
  },
  PEN: {
    locale: "es-PE",
  },
  PHP: {
    locale: "fil-PH",
  },
  PLN: {
    locale: "pl-PL",
  },
  QAR: {
    locale: "ar-QA",
  },
  MKD: {
    locale: "mk-MK",
  },
  RON: {
    locale: "ro-RO",
  },
  RUB: {
    locale: "ru-RU",
  },
  RWF: {
    locale: "rw-RW",
  },
  WST: {
    locale: "sm-WS",
  },
  STN: {
    locale: "pt-ST",
  },
  SAR: {
    locale: "ar-SA",
  },
  RSD: {
    locale: "sr-RS",
  },
  SCR: {
    locale: "fr-SC",
  },
  SLL: {
    locale: "en-SL",
  },
  SGD: {
    locale: "en-SG",
  },
  SBD: {
    locale: "en-SB",
  },
  SOS: {
    locale: "en-US",
  },
  SSP: {
    locale: "en-SS",
  },
  LKR: {
    locale: "si-LK",
  },
  SDG: {
    locale: "ar-SD",
  },
  SRD: {
    locale: "nl-SR",
  },
  SEK: {
    locale: "sv-SE",
  },
  SYP: {
    locale: "ar-SY",
  },
  TWD: {
    locale: "zh-TW",
  },
  TJS: {
    locale: "tg-TJ",
  },
  TZS: {
    locale: "sw-TZ",
  },
  THB: {
    locale: "th-TH",
  },
  TOP: {
    locale: "to-TO",
  },
  TTD: {
    locale: "en-TT",
  },
  TND: {
    locale: "ar-TN",
  },
  TRY: {
    locale: "tr-TR",
  },
  TMT: {
    locale: "tk-TM",
  },
  UGX: {
    locale: "en-UG",
  },
  UAH: {
    locale: "uk-UA",
  },
  AED: {
    locale: "en-US",
  },
  UYU: {
    locale: "es-UY",
  },
  UZS: {
    locale: "uz-Latn-UZ",
  },
  VUV: {
    locale: "bi-VU",
  },
  VEF: {
    locale: "es-VE",
  },
  VND: {
    locale: "vi-VN",
  },
  YER: {
    locale: "ar-YE",
  },
  ZMW: {
    locale: "en-ZM",
  },
  ZWL: {
    locale: "en-ZW",
  },
};
